<section class="email-template-page" aria-label="Supplier invitation email template">
  <app-page-header
    title="Supplier invitation email template"
    description="Customise the tenant image and message shown in supplier invitation emails."
  ></app-page-header>

  <dx-load-panel
    [visible]="loading || imageBusy"
    [showIndicator]="true"
    [showPane]="true"
    [shading]="false"
    [position]="{ of: '.email-template-page' }"
  ></dx-load-panel>

  @if (loadError) {
    <div class="load-error" role="alert">
      <p>{{ templateApiError }}</p>
      <dx-button text="Try again" type="default" (onClick)="retryLoad()"></dx-button>
    </div>
  }

  @if (!loading && !loadError) {
    <div class="template-card image-card">
      <div class="image-card__heading">
        <div>
          <h2>Invitation image</h2>
          <p>A fixed tenant image shown at the top of supplier invitation emails.</p>
        </div>
        @if (savedImage.configured) {
          <span class="configured-badge">Configured</span>
        }
      </div>

      @if (pendingPreviewUrl || savedPreviewUrl) {
        <div class="image-preview">
          @for (previewUrl of [pendingPreviewUrl || savedPreviewUrl]; track previewUrl) {
            <img
              [src]="previewUrl"
              [alt]="previewUrl === pendingPreviewUrl ? 'Selected invitation image preview' : 'Saved invitation image preview'"
              (load)="onPreviewLoaded($event, previewUrl)"
              (error)="onPreviewError(previewUrl)"
            />
          }
        </div>
      }

      @if (imageLoadError) {
        <div class="image-error" role="alert">
          <span>{{ imageApiError || 'The image preview could not be loaded.' }}</span>
          @if (savedImage.configured && !pendingImage) {
            <dx-button text="Try again" stylingMode="text" (onClick)="retryImageLoad()"></dx-button>
          }
        </div>
      } @else if (imageApiError) {
        <p class="api-error" role="alert">{{ imageApiError }}</p>
      }

      <p id="supplier-invitation-image-guidance" class="image-guidance">
        PNG or JPEG, up to 512 KiB and 1600 × 800 pixels.
      </p>
      @if (imageValidationError) {
        <p id="supplier-invitation-image-error" class="image-validation-error" role="alert">
          {{ imageValidationError }}
        </p>
      }
      <label class="file-control">
        <span>{{ savedImage.configured ? 'Select replacement' : 'Select image' }}</span>
        <input
          type="file"
          accept="image/png,image/jpeg"
          [attr.aria-describedby]="imageValidationError
            ? 'supplier-invitation-image-guidance supplier-invitation-image-error'
            : 'supplier-invitation-image-guidance'"
          [attr.aria-invalid]="imageValidationError ? 'true' : null"
          [disabled]="imageBusy"
          (change)="onImageSelected($event)"
        />
      </label>

      @if (pendingImage) {
        <p class="pending-file">Selected: {{ pendingImage.name }}</p>
      }

      <div class="image-actions">
        <dx-button
          text="Save image"
          type="default"
          stylingMode="contained"
          [disabled]="!canSaveImage"
          (onClick)="saveImage()"
        ></dx-button>
        @if (pendingImage) {
          <dx-button
            text="Discard selection"
            stylingMode="outlined"
            [disabled]="imageBusy"
            (onClick)="discardSelectedImage()"
          ></dx-button>
        }
        @if (savedImage.configured) {
          <dx-button
            text="Remove saved image"
            stylingMode="outlined"
            [disabled]="imageBusy"
            (onClick)="removeImage()"
          ></dx-button>
        }
      </div>
    </div>

    <div class="template-card">
      @if (isDefault) {
        <p class="default-note">This scope is currently using the PromoFlo default message.</p>
      }

      <label class="template-label" for="supplier-invitation-email-body">Email body</label>
      <dx-text-area
        [value]="body"
        [height]="320"
        [maxLength]="maxLength"
        [autoResizeEnabled]="false"
        [spellcheck]="true"
        [inputAttr]="{
          id: 'supplier-invitation-email-body',
          'aria-label': 'Supplier invitation email body',
          'aria-describedby': 'email-template-guidance email-template-count'
        }"
        (onValueChanged)="onBodyChanged($event)"
      ></dx-text-area>

      <div class="template-meta">
        <p id="email-template-guidance">
          The secure invitation link is added automatically at the end. Leave this blank to send the link only.
        </p>
        <p id="email-template-count" [class.over-limit]="isOverLimit">
          {{ characterCount | number }} / {{ maxLength | number }} characters
        </p>
      </div>

      @if (templateApiError) {
        <p class="api-error" role="alert">{{ templateApiError }}</p>
      }

      <div class="template-actions">
        <dx-button
          text="Save template"
          type="default"
          stylingMode="contained"
          [disabled]="!canSave"
          (onClick)="save()"
        ></dx-button>
      </div>
    </div>
  }
</section>
